import { useEffect, useRef, useState } from 'react';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useSetupChecklist } from '@/hooks/useSetupChecklist';
import { useChat } from '@/contexts/ChatContext';
import { useRutaAi } from '@/contexts/RutaAiContext';
import { safeLocalStorage, safeSessionStorage } from '@/lib/safeStorage';
import { FIRST_RUN_TOUR_FINISHED_EVENT, firstRunTourPendingKey } from '@/components/onboarding/FirstRunTour';
import { usePostImportSetupModalAnalytics } from '@/hooks/usePostImportSetupModalAnalytics';
import { buildSetupIntro } from './setupIntroMessage';

/**
 * Auto-opens the Ruta AI panel with a scripted setup-checklist welcome for new
 * owners (RUT-2955). Renders nothing — it's a mount-once side effect placed inside
 * the chat + AI provider tree in App.tsx.
 *
 * Behavior:
 * - Fires only when the checklist is `active` (owner + present + not all complete).
 *   The server only returns the checklist for owners on a non-dismissed checklist,
 *   so non-owners never even reach this branch — the panel never flashes for them.
 * - Once per session, keyed by orgId in sessionStorage (NOT localStorage): the
 *   requirement is "every time they come into the app", i.e. once per session.
 * - Displays a scripted assistant message (no LLM round-trip), then opens the panel
 *   with NO seed (a seed would auto-submit a user prompt instead of showing the
 *   script). Closing the panel won't reopen it until the next session.
 * - A brand-new owner gets the guided tour first (RUT-3782): while that tour is
 *   pending this stays quiet, and when the tour finishes it opens with a short
 *   "ask me anything" greeting instead of the full checklist.
 */
export function SetupIntroGate() {
  const { orgId } = useAuthStatus();
  const { checklist, active } = useSetupChecklist();
  usePostImportSetupModalAnalytics(active);
  const { openAi } = useRutaAi();
  const { startNewConversation, addMessage } = useChat();
  // Guards a re-open within this mount (the effect re-runs as org data settles).
  const firedRef = useRef(false);

  // Re-run the effect once the first-run tour reports it is finished.
  const [tourFinished, setTourFinished] = useState(false);
  useEffect(() => {
    const onFinished = () => setTourFinished(true);
    window.addEventListener(FIRST_RUN_TOUR_FINISHED_EVENT, onFinished);
    return () => window.removeEventListener(FIRST_RUN_TOUR_FINISHED_EVENT, onFinished);
  }, []);

  useEffect(() => {
    if (firedRef.current) return;
    if (!active || !orgId || !checklist) return;

    const storageKey = `ruta-setup-intro-shown_${orgId}`;
    if (safeSessionStorage.getItem(storageKey) === 'true') return;

    // The guided tour goes first; we come in right after it.
    const tourPending = safeLocalStorage.getItem(firstRunTourPendingKey(orgId)) === 'true';
    if (tourPending && !tourFinished) return;

    firedRef.current = true;

    const { content, navigationActions, suggestions } = buildSetupIntro(checklist);
    startNewConversation();
    const now = new Date();
    addMessage({
      id: now.getTime().toString(),
      role: 'assistant',
      content: tourFinished
        ? "Hey — I'm Ruta AI. Ask me anything: where something is, how to set it up, or what to do next. I can also take you straight there."
        : content,
      navigationActions: navigationActions.length ? navigationActions : undefined,
      suggestions,
      createdAt: now,
    });
    openAi();
    safeSessionStorage.setItem(storageKey, 'true');
  }, [active, orgId, checklist, tourFinished, startNewConversation, addMessage, openAi]);

  return null;
}
